/**
 * Strategic Funds Module Page (Redesigned - Korat Health Style)
 *
 * 7 Strategic funds with performance tracking
 */

import { useState } from 'react'
import {
  Target,
  TrendingUp,
  Award,
  BarChart3,
  CheckCircle,
  Heart,
  Building2,
  Truck,
  ShieldCheck,
  Users,
  Database,
  Beaker,
  type LucideIcon,
} from 'lucide-react'
import {
  Sidebar,
  PageHeader,
  KpiCardRich,
  DataTable,
  ProgressBar,
  YearFilter,
  DistrictFilter,
  type Column,
} from '../shared/components'
import { BarChartCard } from '../shared/components/charts'
import { useStrategicFundsOverview, useStrategicFundsByDistrict, useStrategicFundsPerformance, useDistricts } from '../services/api'
import type { StrategicFund } from '../types/modules'
import { formatNumber } from '../shared/utils/formatNumber'

// Icon mapper for strategic funds
const getStrategicIcon = (iconName: string): LucideIcon => {
  const iconMap: Record<string, LucideIcon> = {
    'heart-pulse': Heart,        // HeartPulse not available, use Heart
    hospital: Building2,          // Hospital not available, use Building2
    ambulance: Truck,             // Ambulance not available, use Truck
    'shield-check': ShieldCheck,
    users: Users,
    database: Database,
    flask: Beaker,                // Flask not available, use Beaker
  }
  return iconMap[iconName] || Target
}

// Icon colors based on strategy priority (1-7)
const getStrategicColor = (strategyNumber: number): string => {
  const colors = [
    'bg-red-100 text-red-600',           // Strategy 1 - Critical health
    'bg-korat-navy-100 text-korat-navy-700',  // Strategy 2 - Hospital
    'bg-korat-orange-100 text-korat-orange-600', // Strategy 3 - Emergency
    'bg-green-100 text-green-600',       // Strategy 4 - Prevention
    'bg-purple-100 text-purple-600',     // Strategy 5 - Community
    'bg-blue-100 text-blue-600',         // Strategy 6 - Data
    'bg-pink-100 text-pink-600',         // Strategy 7 - Innovation
  ]
  return colors[(strategyNumber - 1) % colors.length]
}

export function StrategicPage() {
  const [selectedYear, setSelectedYear] = useState(2567)
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null)

  const overviewQuery = useStrategicFundsOverview(selectedYear)
  const districtQuery = useStrategicFundsByDistrict(selectedDistrict || 0, selectedYear)

  const data = selectedDistrict ? districtQuery.data : overviewQuery.data
  const isLoading = selectedDistrict ? districtQuery.isLoading : overviewQuery.isLoading
  const error = selectedDistrict ? districtQuery.error : overviewQuery.error

  const { data: districts = [], isLoading: districtsLoading } = useDistricts()

  // Fetch performance data for chart
  const { data: performanceData, isLoading: performanceLoading } = useStrategicFundsPerformance(selectedYear)

  // Calculate budget values
  const totalAllocated = parseFloat(data?.totals.grand_allocated || '0')
  const totalUtilized = parseFloat(data?.totals.grand_utilized || '0')
  const remainingBudget = totalAllocated - totalUtilized
  const strategy1Budget = totalAllocated * 0.25
  const strategy23Budget = totalAllocated * 0.35
  const strategy47Budget = totalAllocated * 0.40

  const columns: Column<StrategicFund>[] = [
    {
      header: 'ยุทธศาสตร์',
      accessor: 'name_th',
      width: 'w-1/3',
      render: (value, row) => {
        const Icon = getStrategicIcon(row.icon_name)
        const colorClass = getStrategicColor(row.strategy_number)
        return (
          <div className="flex items-center gap-3">
            <div className={`rounded-lg p-2 ${colorClass}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <div className="font-semibold text-korat-navy-700 tabular-nums">
                {row.strategy_number}. {value as string}
              </div>
              <div className="text-xs text-slate-500">{row.short_name_th}</div>
            </div>
          </div>
        )
      },
    },
    {
      header: 'งบทั้งหมด',
      accessor: 'total_allocated',
      align: 'right',
      render: (value) => <span className="text-korat-navy-700 tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'ใช้ไปแล้ว',
      accessor: 'total_utilized',
      align: 'right',
      render: (value) => <span className="tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'อัตราการใช้',
      accessor: 'avg_utilization',
      align: 'right',
      width: 'w-32',
      render: (value) => (
        <ProgressBar
          value={parseFloat(value as string)}
          variant={parseFloat(value as string) >= 80 ? 'success' : 'warning'}
        />
      ),
    },
    {
      header: 'ประสิทธิภาพ',
      accessor: 'avg_performance',
      align: 'right',
      width: 'w-32',
      render: (value) => (
        <span className="font-semibold text-emerald-600 tabular-nums">
          {parseFloat(value as string).toFixed(1)}%
        </span>
      ),
    },
  ]

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar currentPage="strategic" />

      <main className="flex-1 overflow-auto">
        <PageHeader
          icon={Target}
          title="ยุทธศาสตร์ทั้ง 7"
          showFilters={true}
          selectedDistrict={
            selectedDistrict ? districts.find((d) => d.id === selectedDistrict)?.name_th : null
          }
          filters={
            <>
              <YearFilter selectedYear={selectedYear} onYearChange={setSelectedYear} />
              <DistrictFilter
                selectedDistrict={selectedDistrict}
                onDistrictChange={setSelectedDistrict}
                districts={districts}
                isLoading={districtsLoading}
              />
            </>
          }
        />

        <div className="p-6 space-y-6">
          {/* KPI Cards */}
          <section>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Total Allocated Budget */}
              <KpiCardRich
                icon={Target}
                title="งบทั้งหมด"
                description="งบประมาณที่ได้รับจัดสรรสำหรับยุทธศาสตร์ทั้ง 7 ด้าน"
                value={data?.totals.grand_allocated || 0}
                unit="บาท"
                helperText="กระจายไปยัง 7 ยุทธศาสตร์หลัก"
                breakdown={[
                  `<b>ยุทธศาสตร์ที่ 1</b> ${formatNumber(strategy1Budget)}`,
                  `<b>ยุทธศาสตร์ที่ 2-3</b> ${formatNumber(strategy23Budget)}`,
                  `<b>ยุทธศาสตร์ที่ 4-7</b> ${formatNumber(strategy47Budget)}`,
                ]}
                isLoading={isLoading}
                variant="navy"
              />

              {/* Total Utilized */}
              <KpiCardRich
                icon={TrendingUp}
                title="ใช้ไปแล้ว"
                description="จำนวนงบประมาณที่ใช้จ่ายไปแล้วทั้งหมด"
                value={data?.totals.grand_utilized || 0}
                unit="บาท"
                helperText={`คงเหลือ ${formatNumber(remainingBudget)} บาท`}
                breakdown={[
                  `<b>งบทั้งหมด</b> ${formatNumber(data?.totals.grand_allocated || 0)}`,
                  `<b>ใช้ไป</b> ${formatNumber(data?.totals.grand_utilized || 0)}`,
                  `<b>คงเหลือ</b> ${formatNumber(remainingBudget)}`,
                ]}
                isLoading={isLoading}
                variant="orange"
              />

              {/* Average Utilization */}
              <KpiCardRich
                icon={BarChart3}
                title="อัตราการใช้เฉลี่ย"
                description="เปอร์เซ็นต์การใช้จ่ายงบประมาณเฉลี่ยของทั้ง 7 ยุทธศาสตร์"
                value={parseFloat(data?.totals.overall_utilization || '0')}
                unit="%"
                helperText="เป้าหมาย 95% ภายในสิ้นปีงบประมาณ"
                breakdown={[
                  `<b>&gt;90%</b> ${data?.strategies?.filter(s => parseFloat(s.avg_utilization.toString()) >= 90).length || 0} ยุทธศาสตร์`,
                  `<b>80-90%</b> ${data?.strategies?.filter(s => parseFloat(s.avg_utilization.toString()) >= 80 && parseFloat(s.avg_utilization.toString()) < 90).length || 0} ยุทธศาสตร์`,
                  `<b>&lt;80%</b> ${data?.strategies?.filter(s => parseFloat(s.avg_utilization.toString()) < 80).length || 0} ยุทธศาสตร์`,
                ]}
                isLoading={isLoading}
                variant="blue"
              />

              {/* Average Performance */}
              <KpiCardRich
                icon={Award}
                title="ประสิทธิภาพเฉลี่ย"
                description="คะแนนประสิทธิภาพเฉลี่ยในการบรรลุเป้าหมาย"
                value={parseFloat(data?.totals.overall_performance || '0')}
                unit="%"
                helperText="ประเมินจากตัวชี้วัดความสำเร็จ (KPI)"
                breakdown={[
                  `<b>ดีเยี่ยม (&gt;90%)</b> ${data?.strategies?.filter(s => parseFloat(s.avg_performance.toString()) >= 90).length || 0} ยุทธศาสตร์`,
                  `<b>ดี (80-90%)</b> ${data?.strategies?.filter(s => parseFloat(s.avg_performance.toString()) >= 80 && parseFloat(s.avg_performance.toString()) < 90).length || 0} ยุทธศาสตร์`,
                  `<b>ปรับปรุง (&lt;80%)</b> ${data?.strategies?.filter(s => parseFloat(s.avg_performance.toString()) < 80).length || 0} ยุทธศาสตร์`,
                ]}
                isLoading={isLoading}
                variant="orange"
              />
            </div>
          </section>

          {/* Strategies Table */}
          <section>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-korat-navy-700 tabular-nums mb-4">
                รายละเอียดทั้ง 7 ยุทธศาสตร์
              </h2>
              <DataTable
                columns={columns}
                data={data?.strategies || []}
                isLoading={isLoading}
                error={error}
                emptyMessage="ไม่พบข้อมูลยุทธศาสตร์"
              />
            </div>
          </section>

          {/* Performance Ranking */}
          <section>
            <div className="bg-white rounded-xl border-2 border-korat-navy-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-korat-navy-700 tabular-nums mb-4">
                อันดับประสิทธิภาพ
              </h2>
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-16 bg-slate-200 rounded animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {data?.strategies
                    ?.slice()
                    .sort((a, b) => parseFloat(b.avg_performance.toString()) - parseFloat(a.avg_performance.toString()))
                    .slice(0, 5)
                    .map((strategy, index) => (
                      <div
                        key={strategy.id}
                        className={`flex items-center gap-4 p-4 rounded-lg border ${
                          index === 0
                            ? 'bg-emerald-50 border-emerald-200'
                            : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-korat-navy-100 shrink-0">
                          <span className="text-lg font-bold text-korat-navy-700 tabular-nums">
                            {index + 1}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-korat-navy-700 tabular-nums">
                            {strategy.strategy_number}. {strategy.name_th}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            งบ {formatNumber(strategy.total_allocated)} • ใช้ {parseFloat(strategy.avg_utilization.toString()).toFixed(1)}%
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {index === 0 && <CheckCircle className="w-5 h-5 text-emerald-600" />}
                          <span className="text-lg font-bold text-emerald-600 tabular-nums">
                            {parseFloat(strategy.avg_performance.toString()).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </section>

          {/* Performance Comparison Chart */}
          <section>
              <BarChartCard
                title="เปรียบเทียบอัตราการใช้งบประมาณ 7 ยุทธศาสตร์"
                description="อัตราการใช้จ่ายงบประมาณเป็นเปอร์เซ็นต์ของแต่ละยุทธศาสตร์"
                data={
                  performanceData?.strategy_ranking
                    ? performanceData.strategy_ranking.map((item: any) => {
                        const utilization = parseFloat(item.avg_utilization || item.utilization || 0)
                        let color = '#1e3a8a' // navy
                        if (utilization >= 90) color = '#10b981' // green
                        else if (utilization >= 70) color = '#ea580c' // orange
                        else color = '#ef4444' // red

                        return {
                          name: `ยุทธศาสตร์ ${item.strategy_number}`,
                          value: utilization,
                          color,
                        }
                      })
                    : []
                }
                horizontal={true}
                height={400}
                isLoading={performanceLoading}
              />

              <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-emerald-500" />
                  <span className="text-slate-600">≥ 90% (ดีเยี่ยม)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-korat-orange-600" />
                  <span className="text-slate-600">70-89% (ดี)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-500" />
                  <span className="text-slate-600">&lt; 70% (ต้องปรับปรุง)</span>
                </div>
              </div>
          </section>
        </div>
      </main>
    </div>
  )
}
